import { Add, Delete } from "@material-ui/icons";
import {
  Fab,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Modal from "components/modal/Modal";
import ModalSubmitFooter from "components/modal/ModalSubmitFooter";
import Select from "components/select/Select";
import { FieldArray, FormikProvider, useFormik } from "formik";
import { groupBy } from "lodash";
import { useGetListProvince } from "service/query/common.query";
import { useCreateQueue, useEditQueue } from "service/query/queue.query";
import { getSelectFieldProps, getTextFieldProps } from "ultis/form";
import * as Yup from "yup";

import SelectExtension from "./SelectExtension";

const QueueModal = ({ open, onClose, currentItem }) => {
  const { data: listProvince } = useGetListProvince();

  const createQueueQuery = useCreateQueue();
  const editExtensionQuery = useEditQueue(currentItem?.id);

  const handleSaveQueue = async (values) => {
    const payload = {
      ...values,
      members: values.members.reduce((res, item) => {
        item.extension.forEach((e) => {
          res.push({
            priority: item.priority,
            id: e.value,
          });
        });
        return res;
      }, []),
    };
    if (currentItem) {
      editExtensionQuery.mutate(
        {
          ...currentItem,
          ...payload,
        },
        {
          onSuccess: onClose,
        }
      );
    } else {
      createQueueQuery.mutate(payload, {
        onSuccess: onClose,
      });
    }
  };

  const formik = useFormik({
    initialValues: {
      name: currentItem?.name || "",
      timeout: currentItem?.attributes?.timeout || "",
      strategy: currentItem?.attributes?.strategy || "rrmemory",
      members: currentItem?.members
        ? Object.entries(
            groupBy(currentItem?.members, (item) => item.priority)
          ).reduce((res, [key, value]) => {
            res.push({
              priority: key,
              extension: value.map((e) => ({
                label: `${e.name} - ${e.username}`,
                value: e.id,
              })),
            });
            return res;
          }, [])
        : [],
      subOrg: currentItem?.subOrg || "",
      note: currentItem?.note || "",
    },
    validationSchema: Yup.object().shape({
      subOrg: Yup.string().required("Không được để trống"),
      name: Yup.string()
        .required("Không được để trống")
        .max(50, "Không được vượt quá 50 ký tự"),
      timeout: Yup.number()
        .required("Không được để trống")
        .typeError("Chỉ được nhập số")
        .integer("Chỉ được nhập số nguyên")
        .min(1, "Số lớn hơn 0"),
      members: Yup.array()
        .of(
          Yup.object().shape({
            priority: Yup.number()
              .required("Không được để trống")
              .typeError("Chỉ được nhập số")
              .integer("Chỉ được nhập số nguyên")
              .min(0, "Số bắt đầu từ 0")
              .max(9, "Số lớn nhất bằng 9"),
            extension: Yup.array().min(1, "Không được để trống"),
          })
        )
        .required("Required"),
    }),
    onSubmit: handleSaveQueue,
  });

  return (
    <Modal
      open={open}
      title={currentItem ? "Chỉnh sửa hàng đợi" : "Thêm hàng đợi"}
      onClose={onClose}
      maxWidth="md">
      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={2}>
          <Select
            {...getSelectFieldProps(formik, "subOrg")}
            label="Bộ phận"
            options={listProvince?.map((item) => ({
              label: item.name,
              value: item.code,
            }))}
            allowClear
          />

          <TextField
            {...getTextFieldProps(formik, "name")}
            label="Tên hàng đợi"
            variant="outlined"
            fullWidth
          />

          <TextField
            {...getTextFieldProps(formik, "timeout")}
            label="Thời gian trượt giữa các máy lẻ"
            variant="outlined"
            fullWidth
          />

          <Select
            {...getSelectFieldProps(formik, "strategy")}
            label="Cách thức phân bổ cuộc gọi"
            options={[
              { label: "Rung tất cả máy lẻ", value: "ringall" },
              {
                label: "Xoay vòng lần lượt các máy lẻ",
                value: "rrmemory",
              },
              {
                label: "Máy lẻ nhận ít cuộc gọi nhất",
                value: "fewestcalls",
              },
              {
                label: "Máy lẻ chưa nhận cuộc gọi dài nhất",
                value: "leastrecent",
              },
              {
                label: "Ngẫu nhiên",
                value: "random",
              },
            ]}
            showSearch={false}
          />

          <FormikProvider value={formik}>
            <FieldArray
              name="members"
              render={(arrayHelpers) => (
                <div>
                  <Typography>
                    Sắp xếp máy lẻ vào hàng đợi theo độ ưu tiên
                  </Typography>
                  {formik.values.members &&
                    formik.values.members.length > 0 &&
                    formik.values.members.map((_item, index) => (
                      <Grid container key={index} spacing={2} my={0.5}>
                        <Grid item xs={2}>
                          <TextField
                            {...getTextFieldProps(
                              formik,
                              `members.${index}.priority`
                            )}
                            error={
                              !!formik.touched.members?.[index]?.priority &&
                              !!formik.errors.members?.[index]?.priority
                            }
                            helperText={
                              !!formik.touched.members?.[index]?.priority &&
                              formik.errors.members?.[index]?.priority
                            }
                            variant="standard"
                            label="Ưu tiên"
                          />
                        </Grid>
                        <Grid item xs={8}>
                          <SelectExtension
                            isMulti
                            value={formik.values.members?.[index]?.extension}
                            onChange={(newValue) =>
                              formik.setFieldValue(
                                `members.${index}.extension`,
                                newValue
                              )
                            }
                            error={
                              formik.touched.members?.[index]?.extension &&
                              formik.errors.members?.[index]?.extension
                            }
                            label="Máy lẻ"
                            excludes={formik.values.members.flatMap(
                              (item) => item.extension
                            )}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={2}
                          sx={{ display: "flex", alignItems: "center" }}>
                          <IconButton
                            onClick={() => arrayHelpers.remove(index)}>
                            <Delete />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                  <Stack direction="row" spacing={2} alignItems="center" mt={1}>
                    <Typography>Thêm độ ưu tiên</Typography>
                    <Tooltip title="Thêm queue">
                      <Fab
                        color="primary"
                        size="small"
                        onClick={() =>
                          arrayHelpers.push({
                            priority: "",
                            extension: [],
                          })
                        }>
                        <Add />
                      </Fab>
                    </Tooltip>
                  </Stack>
                </div>
              )}
            />
          </FormikProvider>

          <TextField {...getTextFieldProps(formik, "note")} label="Ghi chú" />
        </Stack>

        <ModalSubmitFooter
          onCancel={onClose}
          loading={createQueueQuery.isPending || editExtensionQuery.isPending}
        />
      </form>
    </Modal>
  );
};

export default QueueModal;
